import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Form as AntdForm } from 'antd';
import { required } from '../../../../antd/form/FormValidation';
import PhdFormBuilder from '../PhdFormBuilder';
import {
  STUDY_FORM_OPTION_KEYS,
  translateOptions,
} from '../phdFormOptions.jsx';
import { createSchoolYearChangeHandler } from '../utils/usePhdSchoolYearOptions';
import {
  getPhdDocumentDependencyState,
  PHD_DOCUMENT_DEPENDENCY_TYPES,
} from '../utils/phdDocumentDependencies.jsx';

const PhdDissertationExamApplicationsEditForm = ({
  studentOptions,
  students,
  schoolYearOptions,
  studyFieldLabelOptions,
  ensureSchoolYearSelection,
  isCreatingSchoolYear,
  isSubmitting,
  isStudentContext = false,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const form = AntdForm.useFormInstance();
  const selectedStudentId = AntdForm.useWatch('phd_student_id', form);
  const selectedSchoolYearId = AntdForm.useWatch('phd_school_year_id', form);
  const handleSchoolYearChange = createSchoolYearChangeHandler(
    form,
    ensureSchoolYearSelection,
  );
  const studyFormOptions = useMemo(
    () => translateOptions(STUDY_FORM_OPTION_KEYS, t),
    [t],
  );
  const selectedStudent = useMemo(
    () =>
      (students || []).find(
        (student) => Number(student.id) === Number(selectedStudentId),
      ),
    [selectedStudentId, students],
  );
  const dependencyState = useMemo(
    () =>
      getPhdDocumentDependencyState({
        documentType:
          PHD_DOCUMENT_DEPENDENCY_TYPES.DISSERTATION_EXAM_APPLICATION,
        student: selectedStudent,
        schoolYearId: selectedSchoolYearId,
      }),
    [selectedSchoolYearId, selectedStudent],
  );

  const sections = useMemo(
    () => [
      {
        key: 'basic',
        title: t('Základné informácie'),
        fields: [
          !isStudentContext && {
            name: 'phd_student_id',
            label: t('Študent'),
            type: 'select',
            options: studentOptions,
            rules: [required(t)],
            colSpan: 12,
          },
          {
            name: 'phd_school_year_id',
            label: t('Školský rok'),
            type: 'select',
            options: schoolYearOptions,
            componentProps: {
              onChange: handleSchoolYearChange,
              loading: isCreatingSchoolYear,
            },
            rules: [required(t)],
            colSpan: 12,
          },
          {
            name: 'thesis_title_sk',
            label: t('Názov témy (SK)'),
            type: 'text',
            rules: [required(t)],
            colSpan: 12,
          },
          {
            name: 'thesis_title_en',
            label: t('Názov témy (EN)'),
            type: 'text',
            rules: [required(t)],
            colSpan: 12,
          },
        ].filter(Boolean),
      },
      {
        key: 'student',
        title: t('Údaje o študentovi'),
        fields: [
          {
            name: 'student_display_name',
            label: t('Meno študenta'),
            type: 'text',
            colSpan: 12,
            componentProps: { disabled: isStudentContext },
          },
          {
            name: 'supervisor_display_name',
            label: t('Školiteľ'),
            type: 'text',
            colSpan: 12,
            componentProps: { disabled: isStudentContext },
          },
          {
            name: 'student_study_form',
            label: t('Forma štúdia'),
            type: 'select',
            options: studyFormOptions,
            colSpan: 12,
            componentProps: { disabled: isStudentContext },
          },
          {
            name: 'student_study_field',
            label: t('Študijný odbor'),
            type: 'text',
            colSpan: 12,
            componentProps: { disabled: isStudentContext },
          },
          {
            name: 'student_study_program',
            label: t('Študijný program'),
            type: 'select',
            options: studyFieldLabelOptions,
            colSpan: 24,
            componentProps: { disabled: isStudentContext },
          },
        ],
      },
    ],
    [
      t,
      studentOptions,
      schoolYearOptions,
      studyFieldLabelOptions,
      studyFormOptions,
      isStudentContext,
      handleSchoolYearChange,
      isCreatingSchoolYear,
    ],
  );

  return (
    <PhdFormBuilder
      sections={sections}
      submitKey="phd-dissertation-exam-applications"
      isSubmitting={isSubmitting}
      isSubmitDisabled={dependencyState.missingItems.length > 0}
      hideSubmit={isReadOnly}
      beforeSubmitContent={
        dependencyState.message ? (
          <Alert
            type="warning"
            showIcon
            message={t('Chýbajúce podklady')}
            description={dependencyState.message}
            style={{ marginTop: 16 }}
          />
        ) : null
      }
    />
  );
};

export default PhdDissertationExamApplicationsEditForm;
